import React, { useContext, useState, useEffect, useRef } from 'react';
import { ThemeContext } from '../../hooks/ThemeContext';
import { api } from '../../services/baseUrl';
import LoadingSpinner from '../globals/LoadingSpinner';
import { ArrowLeft, Send, Trash2, XCircle, User, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const MessageDetail = ({ conversation, onBack, onUpdate }) => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === 'dark';
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [canReply, setCanReply] = useState(true);
  const [conversationType, setConversationType] = useState('creator_conversation');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (conversation) {
      fetchMessages();
    }
  }, [conversation]);

  useEffect(() => {
    // Scroll vers le bas à chaque nouveau message
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/conversations/${conversation.conversation_id}/messages`);
      if (response.data.success) {
        setMessages(response.data.messages || []);
        setCanReply(response.data.can_reply !== false);
        setConversationType(response.data.type || 'creator_conversation');
        // Notifier le parent pour mettre à jour le badge
        if (onUpdate) onUpdate();
        window.dispatchEvent(new Event('messagesUpdated'));
      }
    } catch (err) {
      console.error('Erreur chargement messages:', err);
      toast.error('Erreur lors du chargement des messages');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim()) {
      toast.error('Le message ne peut pas être vide');
      return;
    }

    // Empêcher l'envoi si c'est un message contact admin
    if (conversationType === 'admin_contact') {
      toast.error('Vous ne pouvez pas répondre directement. L\'admin vous répondra par email.');
      return;
    }

    try {
      setSending(true);
      const response = await api.post('/conversations/send', {
        receiver_id: conversation.other_user.id,
        content: newMessage.trim(),
        conversation_id: conversation.conversation_id,
      });

      if (response.data.success) {
        setNewMessage('');
        fetchMessages(); // Recharger les messages
        toast.success('Message envoyé');
      }
    } catch (err) {
      console.error('Erreur envoi message:', err);
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setSending(false);
    }
  };

  const closeConversation = async () => {
    // Ne pas permettre de clôturer les messages contact admin
    if (conversationType === 'admin_contact') {
      toast.error('Vous ne pouvez pas clôturer ce type de message');
      return;
    }

    if (!window.confirm('Voulez-vous vraiment clôturer cette conversation ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`https://api.krayneagency.fr/api/conversations/${conversation.conversation_id}/close`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Conversation clôturée');
        if (onUpdate) onUpdate();
        onBack();
      } else {
        toast.error(data.message || 'Erreur lors de la clôture');
      }
    } catch (err) {
      console.error('Erreur clôture conversation:', err);
      toast.error('Erreur lors de la clôture');
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('Supprimer ce message ?')) {
      return;
    }

    try {
      const response = await api.delete(`/conversations/messages/${messageId}`);
      if (response.data.success) {
        toast.success('Message supprimé');
        fetchMessages();
      }
    } catch (err) {
      console.error('Erreur suppression:', err);
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className={`flex flex-col h-[600px] rounded-lg border ${
      isDark ? 'bg-[#0b0b0c] border-white/6' : 'bg-white border-gray-100'
    }`}>
      {/* Header */}
      <div className={`flex items-center justify-between p-4 border-b ${
        isDark ? 'border-white/6' : 'border-gray-100'
      }`}>
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className={`p-2 rounded-lg transition-colors ${
              isDark ? 'hover:bg-white/5' : 'hover:bg-gray-100'
            }`}
          >
            <ArrowLeft size={20} />
          </button>

          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            isDark ? 'bg-gradient-to-br from-purple-500/20 to-blue-500/20' : 'bg-gradient-to-br from-purple-100 to-blue-100'
          }`}>
            <User size={20} className={isDark ? 'text-purple-300' : 'text-purple-600'} />
          </div>

          <div>
            <h3 className="font-semibold">{conversation.other_user.name}</h3>
            <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              {conversation.other_user.role === 2 ? 'Créateur' : 'Utilisateur'}
            </p>
          </div>
        </div>

        <button
          onClick={closeConversation}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
            isDark 
              ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20' 
              : 'bg-red-50 text-red-600 hover:bg-red-100'
          }`}
        >
          <XCircle size={16} />
          Clôturer
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg) => {
          // Déterminer si c'est mon message en comparant avec l'ID de l'autre utilisateur
          const isMe = msg.sender?.id_user !== conversation.other_user.id;
          const senderName = msg.sender?.name || msg.sender?.nickname || 'Utilisateur';
          
          return (
            <div
              key={msg.id_message || msg.id}
              className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[70%] ${isMe ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                {/* Nom de l'expéditeur */}
                {!isMe && (
                  <span className={`text-xs font-medium px-1 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                    {senderName}
                  </span>
                )}
                
                <div
                  className={`p-3 rounded-2xl ${
                    isMe
                      ? isDark
                        ? 'bg-gradient-to-r from-purple-500/20 to-blue-500/20 text-white'
                        : 'bg-gradient-to-r from-purple-500 to-blue-500 text-white'
                      : isDark
                      ? 'bg-white/5 text-white'
                      : 'bg-gray-100 text-gray-900'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                </div>

                <div className="flex items-center gap-2">
                  <span className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                    {formatDate(msg.created_at)}
                  </span>
                  
                  {msg.read_at && isMe && (
                    <CheckCircle size={12} className="text-blue-500" />
                  )}

                  {isMe && (
                    <button
                      onClick={() => deleteMessage(msg.id_message || msg.id)}
                      className={`text-xs opacity-60 hover:opacity-100 transition-opacity`}
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input - Affiché uniquement si l'utilisateur peut répondre */}
      {canReply && conversationType !== 'admin_contact' && (
        <form onSubmit={sendMessage} className={`p-4 border-t ${
          isDark ? 'border-white/6' : 'border-gray-100'
        }`}>
          <div className="flex gap-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Écrire un message..."
              disabled={sending}
              className={`flex-1 px-4 py-2 rounded-lg outline-none transition-all ${
                isDark
                  ? 'bg-white/5 border border-white/10 focus:border-white/20 text-white placeholder:text-white/40'
                  : 'bg-gray-50 border border-gray-200 focus:border-gray-300 text-gray-900 placeholder:text-gray-400'
              }`}
            />
            <button
              type="submit"
              disabled={sending || !newMessage.trim()}
              className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                sending || !newMessage.trim()
                  ? isDark
                    ? 'bg-white/5 text-white/30 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-300 cursor-not-allowed'
                : isDark
                ? 'bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white'
                : 'bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white'
            }`}
          >
            <Send size={18} />
            {sending ? 'Envoi...' : 'Envoyer'}
          </button>
        </div>
      </form>
      )}

      {/* Message si contact admin */}
      {conversationType === 'admin_contact' && (
        <div className={`p-4 border-t text-center ${
          isDark ? 'border-white/6 bg-blue-500/10' : 'border-gray-100 bg-blue-50'
        }`}>
          <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
            💬 Vous avez contacté le support. L'équipe vous répondra par email.
          </p>
        </div>
      )}
    </div>
  );
};

export default MessageDetail;
